import React, { useState, useEffect, useCallback } from 'react';

import { t } from 'i18next';

import { CHEVRON_DOWN_V3 } from '../../../constants/images';
import { IBdsComboBox } from '../../../interfaces/bdsComboBox';
import { BdsSelectionItem } from '../BdsSelectionItem/BdsSelectionItem';
import BdsIconButton from '../Buttons/BdsIconButton/BdsIconButton';
import BdsDivider from '../Globals/BdsDivider/BdsDivider';
import './BdsComboBox.scss';

const BdsComboBox = ({ items, onChange, placeHolder, value, onCollapse }: IBdsComboBox) => {
  const [selected, setSelected] = useState<string>(value || '');
  const [openList, setOpenList] = useState(false);
  const [filteredItems, setFilteredItems] = useState(items);
  const [inputValue, setInputValue] = useState('');

  const filterItems = useCallback(
    (search: string) => {
      if (search === '') {
        return items;
      }
      const normalizedSearch = search.trim().toLowerCase();
      return items.filter((item) => item.title.toLowerCase().trim().includes(normalizedSearch));
    },
    [items]
  );

  useEffect(() => {
    const selectedItem = items.find((item) => item.value === selected);
    setInputValue(selectedItem ? selectedItem.title : '');
  }, [selected, items]);

  useEffect(() => {
    setFilteredItems(filterItems(inputValue));
  }, [inputValue, filterItems]);

  const handleOnChange = (value: string) => {
    const selectedItem = items.find((item) => item.value === value);
    setSelected(value);
    setInputValue(selectedItem ? selectedItem.title : '');
    onChange(value);
    setOpenList(false);
    onCollapse?.(false);
  };

  const handleOnChangeInput = (newInputValue: string) => {
    setInputValue(newInputValue);
    setOpenList(true);
    onCollapse?.(true);
  };

  const handleIconClick = () => {
    setOpenList(!openList);
    onCollapse?.(!openList);
  };

  const handleInputClick = () => {
    if (!openList) {
      setOpenList(true);
      onCollapse?.(true);
    }
  };

  return (
    <div className={`bds-combo-box ${openList ? 'open' : ''}`}>
      <div className="placeholder-container">
        <div className="input-container">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => handleOnChangeInput(e.target.value)}
            onClick={handleInputClick}
            placeholder={placeHolder}
            className="cb-input"
          />
          <BdsIconButton
            className={`icon-button ${openList ? 'open' : ''}`}
            icon={CHEVRON_DOWN_V3}
            variant="transparent"
            action={handleIconClick}
            size="sm"
          />
        </div>
        <BdsDivider color={selected ? 'high' : 'subtle'} size="s" />
      </div>

      <div className={`list-selection-item ${openList ? 'open' : ''}`}>
        {filteredItems.length > 0 ? (
          filteredItems.map((item, index) => (
            <div key={String(item.value)} className={`item-wrapper ${openList ? 'open' : ''}`}>
              <BdsSelectionItem
                leadingData={{
                  title: item.title,
                  subtitle: item.subtitle
                }}
                data={{
                  name: item.value,
                  value: item.value,
                  selected: selected === item.value,
                  onChange: handleOnChange
                }}
              />
              {index < filteredItems.length - 1 && (
                <div className="divider-wrapper">
                  <BdsDivider color="light" size="s" />
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="no-results">{t('global.search.no_results.title')}</div>
        )}
      </div>
    </div>
  );
};

export default BdsComboBox;
